/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import com.benasher44.uuid.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import systems.frictionless.impulse.rest.internal.UuidSerializer
import systems.frictionless.impulse.rest.model.Answer.Companion.MAX_COMMENT_LENGTH

/**
 * Representation of the answer back from the user, after being persisted.
 *
 * Note: The JSON serializer needs `useArrayPolymorphism = false`.
 *
 * @param id The identifier of the answer.
 * @param assessmentId The identifier of the assessment this answer belongs to, associated with [Assessment.id]
 * @param questionId The identifier of the question this answer is for.  Not associated with the database model.
 * @param value The type of answer, which also encapsulates the response from the user.
 * @param comment Free-form comment from the user.
 * @see MAX_COMMENT_LENGTH
 */
@Serializable
data class Answer(
    @Serializable(with = UuidSerializer::class) val id: Uuid,
    @Serializable(with = UuidSerializer::class)
    @SerialName("assessment_id")
    val assessmentId: Uuid,
    @Serializable(with = UuidSerializer::class)
    @SerialName("question_id")
    val questionId: Uuid,
    val value: Type,
    val comment: String?
) {
    init {
        comment?.let {
            require(it.length <= MAX_COMMENT_LENGTH) { "Comment length must be <= $MAX_COMMENT_LENGTH but is ${it.length}" }
        }
    }

    companion object {
        const val MAX_COMMENT_LENGTH = 500

        fun new(
            uuid: Uuid,
            builder: Builder
        ) = Answer(
            uuid,
            assessmentId = builder.assessmentId,
            questionId = builder.questionId,
            builder.value,
            builder.comment
        )
    }

    /**
     * Representation of the answer back from the user, prior to being persisted.
     *
     * Note: The JSON serializer needs `useArrayPolymorphism = false`.
     * @see MAX_COMMENT_LENGTH
     */
    @Serializable
    data class Builder(
        @Serializable(with = UuidSerializer::class)
        @SerialName("assessment_id")
        val assessmentId: Uuid,
        @Serializable(with = UuidSerializer::class)
        @SerialName("question_id")
        val questionId: Uuid,
        val value: Type,
        val comment: String?
    ) {
        init {
            comment?.let {
                require(it.length <= MAX_COMMENT_LENGTH) { "Comment length must be <= $MAX_COMMENT_LENGTH but is ${it.length}" }
            }
        }

        companion object {
            fun new(
                assessment: Assessment,
                question: Question,
                value: Type,
                comment: String?
            ) = Builder(assessment.id, question.id, value, comment)
        }
    }

    @Serializable
    sealed class Type {
        /**
         * @param choice The item's index in the original choice list.
         */
        @Serializable
        @SerialName("mc")
        data class MultipleChoice(val choice: Index) : Answer.Type()

        /**
         * @param likertFour The choice the user selected.
         */
        @Serializable
        @SerialName("likert")
        data class Likert(val likertFour: LikertFour) : Answer.Type()

        /**
         * A free-response solely utilizes the [Answer.comment] field.
         */
        @Serializable
        @SerialName("fr")
        data object FreeResponse : Answer.Type()
    }
}
